import React, { useContext, useEffect } from 'react'

import { useNotifications } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import { isLiteRetailer } from 'store/currentUser/selectors'

import { DATA_ACTIVITY_CENTER, LIST_JOBS } from 'routes/paths'

import Steps from '../common/StepIndicator'
import {
  ImportOrdersModalIds,
  ImportOrdersPopupIds,
} from '../orderImportModal.ids'
import FirstStep from './FirstStep/FirstStep'
import { SimplifiedTypes } from './FirstStep/FirstStep.utils'
import { ImportOrdersContext } from './ImportOrders.context'
import { useImportValidatedOrders } from './ImportOrders.hooks'
import {
  ActionButton,
  ButtonBack,
  ErrorsLabel,
  Footer,
  HorizontalLine,
  ImportingLoader,
  LinkToJobs,
  RedirectText,
} from './ImportOrders.styles'
import { ImportOrdersProps } from './ImportOrders.types'
import SecondStep from './SecondStep/SecondStep'
import ThirdStep from './ThirdStep/ThirdStep'
import UploadFileProgressBar from './UploadFileProgressBar/UploadFileProgressBar'

export const TOTAL_STEPS: number = 3
export const TEMPLATE_SELECTION: number = 0
export const ORDER_SETTINGS: number = 1
export const IMPORT_SUMMARY: number = 2

const ImportOrders: React.FC<ImportOrdersProps> = ({
  closeModal,
  setShowTitleBar,
  setModalTitle,
}) => {
  const {
    currentStep,
    setCurrentStep,
    fileType,
    isNextDisabled,
    importEnabled,
    showUploadBar,
    setShowUploadBar,
    showLoader,
    setShowLoader,
    errorMsg,
    setErrorMsg,
    validatedOrders,
    buttonText,
    setButtonText,
    modalTitle,
    showTitleBar,
    closeOrderImportModal,
    templateType,
    openedDropdown,
  } = useContext(ImportOrdersContext)

  const importValidatedOrders = useImportValidatedOrders()
  const { addToasts } = useNotifications()
  const { trackEvent } = useUserAnalytics()

  const isLiteRetailerUser = useSelector(isLiteRetailer)

  const isNextHidden: boolean = currentStep === TOTAL_STEPS - 1

  const isImportHidden: boolean =
    currentStep !== TOTAL_STEPS - 1 ||
    showLoader ||
    fileType === SimplifiedTypes.Standard

  const isCloseHidden: boolean =
    currentStep !== TOTAL_STEPS - 1 ||
    showLoader ||
    fileType !== SimplifiedTypes.Standard

  const successToastContent: React.ReactNode = (
    <LinkToJobs to={isLiteRetailerUser ? DATA_ACTIVITY_CENTER : LIST_JOBS}>
      <p>
        Orders are being imported. You should be able to see new orders in
        Orders page or Cart soon.
      </p>
      <RedirectText id={ImportOrdersPopupIds.ViewImportedJobsLink}>
        Click this card to view import jobs.
      </RedirectText>
    </LinkToJobs>
  )

  const nextButtonId =
    currentStep === 0
      ? ImportOrdersModalIds.NextButtonFirstStepId
      : templateType?.value !== 'standard'
        ? ImportOrdersModalIds.NextButtonSecondStepFlexibleId
        : ImportOrdersModalIds.NextButtonSecondStepStandardId

  useEffect(() => {
    if (!isImportHidden && !importEnabled) {
      setErrorMsg('Fix or ignore errors to complete import')
    }
    setErrorMsg('')
  }, [isImportHidden, importEnabled, setErrorMsg])

  useEffect(() => {
    setShowTitleBar(showTitleBar)
  }, [showTitleBar, setShowTitleBar])

  useEffect(() => {
    setModalTitle(modalTitle)
  }, [modalTitle, setModalTitle])

  useEffect(() => {
    if (closeOrderImportModal) closeModal()
  }, [closeOrderImportModal, closeModal])

  useEffect(() => {
    setButtonText(
      currentStep === ORDER_SETTINGS &&
        !showUploadBar &&
        fileType === SimplifiedTypes.Standard
        ? 'Import Orders'
        : 'Next',
    )
  }, [currentStep, showUploadBar, fileType, setButtonText])

  const nextStep = (): void => {
    if (currentStep > TOTAL_STEPS) return
    trackEvent('Orders List_Import Orders Modal Click Next')
    if (currentStep === ORDER_SETTINGS && !showUploadBar) {
      setShowUploadBar(true)
    } else if (
      currentStep === TEMPLATE_SELECTION &&
      isLiteRetailerUser &&
      templateType?.value === 'standard'
    ) {
      setShowUploadBar(true)
      setCurrentStep(ORDER_SETTINGS)
    } else {
      setShowUploadBar(false)
      setCurrentStep(currentStep + 1)
    }
  }

  const backStep = (): void => {
    if (!currentStep) return
    trackEvent('Orders List_Import Orders Modal Click Back')
    if (currentStep === ORDER_SETTINGS && showUploadBar) {
      setShowUploadBar(false)
    } else if (currentStep === IMPORT_SUMMARY && isLiteRetailerUser) {
      setCurrentStep(TEMPLATE_SELECTION)
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const importOrders = async () => {
    setShowLoader(true)
    const response = await importValidatedOrders(validatedOrders)
    setShowLoader(false)

    if (response?.errors) {
      addToasts([
        {
          title: 'Error',
          description: response?.errors[0].message,
          type: 'error',
        },
      ])

      closeModal()
    } else {
      addToasts([
        {
          title: 'Import Orders in Process',
          description: successToastContent,
          type: 'success',
          timeOut: 10000,
        },
      ])

      closeModal()
    }
  }

  return (
    <>
      {currentStep === TEMPLATE_SELECTION && (
        <Container
          id={ImportOrdersModalIds.TemplateSelectionContainer}
          data-testid={ImportOrdersModalIds.TemplateSelectionContainer}
          className="scroll"
        >
          <FirstStep />
        </Container>
      )}
      {currentStep === ORDER_SETTINGS && !showUploadBar && (
        <Container
          id={ImportOrdersModalIds.OrderSettingsContainer}
          data-testid={ImportOrdersModalIds.OrderSettingsContainer}
          className={openedDropdown ? '' : 'scroll'}
        >
          <SecondStep />
        </Container>
      )}
      {currentStep === ORDER_SETTINGS && showUploadBar && (
        <UploadFileProgressBar nextStep={nextStep} />
      )}
      {currentStep === IMPORT_SUMMARY && <ThirdStep />}
      <HorizontalLine />
      <ErrorsLabel>{errorMsg}</ErrorsLabel>
      <Footer>
        {currentStep !== TEMPLATE_SELECTION && (
          <ButtonBack onClick={backStep} disabled={showUploadBar}>
            Back
          </ButtonBack>
        )}
        <Steps currentStepIndex={currentStep} totalSteps={TOTAL_STEPS} />
        {!showLoader && !isNextHidden && (
          <ActionButton
            id={nextButtonId}
            onClick={nextStep}
            disabled={isNextDisabled}
          >
            {buttonText}
          </ActionButton>
        )}
        {!showLoader && !isImportHidden && (
          <ActionButton
            onClick={importOrders}
            disabled={!importEnabled}
            data-testid="import-button"
            id={ImportOrdersModalIds.ImportButtonId}
          >
            Import
          </ActionButton>
        )}
        {!isCloseHidden && (
          <ActionButton onClick={closeModal}>Close</ActionButton>
        )}
        {showLoader && <ImportingLoader active={showLoader}>.</ImportingLoader>}
      </Footer>
    </>
  )
}

const Container = styled.div`
  max-height: calc(100vh - 380px);
  padding: 16px;
  background-color: var(--color-secondary-100);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.scroll {
    overflow: auto;
  }
`

export default ImportOrders
